import { browserFetch } from '../../services/browserSession';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle, ArrowRight, Bookmark, CheckCircle2, ChevronDown, ChevronRight, Clock3,
  Gem, LockKeyhole, MapPin, Search, ShieldAlert, SlidersHorizontal, Sparkles, TimerReset, TrendingUp,
  Users, X
} from 'lucide-react';
import { getCountries, getUserProfile } from '../../services/api';
import { useAuth } from '../auth/AuthContext';
import { Button, Badge, CollabTypeBadge, Skeleton } from '../common/design-system';
import styles from './Feed.module.scss';
import clsx from 'clsx';

const CATEGORY_BY_OFFER_TYPE = {
  '1': 'food', '2': 'fashion', '3': 'lifestyle', '4': 'lifestyle', '5': 'lifestyle',
  '6': 'travel', '7': 'wellness', '8': 'beauty', '9': 'travel', '10': 'lifestyle', '11': 'lifestyle'
};

const CATEGORY_BY_TARGET_OPTION = {
  '1': 'lifestyle', '2': 'fashion', '3': 'beauty', '4': 'food', '5': 'beauty',
  '6': 'lifestyle', '7': 'travel', '8': 'beauty', '9': 'lifestyle'
};

const getOfferCategories = offer => {
  const categories = new Set();
  const typeCategory = CATEGORY_BY_OFFER_TYPE[offer.type];
  if (typeCategory) categories.add(typeCategory === 'wellness' ? 'beauty' : typeCategory);
  (offer.target_options || []).forEach(option => {
    const code = typeof option === 'object' ? option.code : option;
    const category = CATEGORY_BY_TARGET_OPTION[code];
    if (category) categories.add(category);
  });
  return categories;
};

const formatOfferValue = offer => {
  const amount = offer.total_creator_value ?? offer.value ?? offer.cash_compensation;
  if (amount === null || amount === undefined || amount === '') return null;
  const currency = (offer.cash_currency || 'USD').toUpperCase();
  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount)) return `${currency} ${amount}`;
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(numericAmount);
  } catch {
    return `${currency} ${numericAmount.toFixed(2)}`;
  }
};

const Feed = () => {
  const { t } = useTranslation(['creator', 'collab']);
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [offers, setOffers] = useState([]);
  const [appliedOffers, setAppliedOffers] = useState([]);
  const [expiredOffers, setExpiredOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingInit, setLoadingInit] = useState(true);
  const [query, setQuery] = useState('');
  const [activeTab, setActiveTab] = useState('available');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('new');
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [savedOffers, setSavedOffers] = useState(() => new Set());
  const [savingOffers, setSavingOffers] = useState(() => new Set());

  const fetchVerificationStatus = useCallback(async () => {
    try {
      if (!currentUser) return;
      const res = await browserFetch('/api/creator/verification/check/');
      if (res.ok) setVerificationStatus(await res.json());
    } catch (err) { console.error('Error fetching verification status:', err); }
  }, [currentUser]);

  const fetchOffers = useCallback(async (countryId = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ country_id: countryId || '', include_expired: 'applied' });
      const res = await browserFetch(`/api/feed/?${params.toString()}`);
      if (!res.ok) throw new Error('Error al cargar el feed');
      const data = await res.json();
      const available = [], applied = [], expired = [], now = new Date();
      data.forEach(offer => {
        const expiryDate = offer.expires_at ? new Date(offer.expires_at) : offer.to_date ? new Date(offer.to_date) : null;
        if (expiryDate && expiryDate < now) expired.push(offer);
        else if (offer.has_applied) applied.push(offer);
        else available.push(offer);
      });
      setOffers(available); setAppliedOffers(applied); setExpiredOffers(expired);
      const serverSavedIds = data.filter(offer => offer.is_saved).map(offer => offer.id);
      if (serverSavedIds.length) {
        setSavedOffers(previous => new Set([...previous, ...serverSavedIds]));
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoadingInit(true);
      try {
        await fetchVerificationStatus();
        const [countriesData, profileData] = await Promise.all([getCountries(), getUserProfile()]);
        const list = countriesData.data || [];
        setCountries(list);
        const profile = profileData.data;
        const initialCountry = profile?.country_details?.id || profile?.country || list[0]?.id || '';
        const normalizedCountry = initialCountry ? initialCountry.toString() : '';
        setSelectedCountry(normalizedCountry);
        await fetchOffers(normalizedCountry);
      } catch (err) { console.error(err); }
      finally { setLoadingInit(false); }
    };
    init();
  }, [fetchOffers, fetchVerificationStatus]);

  const handleCountryChange = event => {
    const countryId = event.target.value;
    setSelectedCountry(countryId);
    fetchOffers(countryId);
  };

  const calculateTimeRemaining = offer => {
    const expiry = offer.expires_at ? new Date(offer.expires_at) : offer.to_date ? new Date(offer.to_date) : null;
    if (!expiry) return t('creator:feed.card.long_duration');
    const diff = expiry - new Date();
    if (diff <= 0) return t('creator:feed.card.expired');
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    if (days > 0) return `${days}d ${hours}h`;
    return t('creator:feed.card.expires_in', { time: `${hours}h` });
  };

  const tabOptions = useMemo(() => ([
    { key: 'available', label: t('creator:feed.sections.available'), help: t('creator:feed.sections.available_help'), count: offers.length },
    { key: 'applied', label: t('creator:feed.sections.applied'), help: t('creator:feed.sections.applied_help'), count: appliedOffers.length },
    { key: 'expired', label: t('creator:feed.sections.expired'), help: t('creator:feed.sections.expired_help'), count: expiredOffers.length },
    { key: 'all', label: t('creator:feed.sections.all'), help: t('creator:feed.sections.all_help'), count: offers.length + appliedOffers.length + expiredOffers.length }
  ]), [appliedOffers.length, expiredOffers.length, offers.length, t]);

  const categoryFilters = useMemo(() => ([
    { key: 'all', label: t('creator:feed.filters.all', 'Todos') },
    { key: 'food', label: t('creator:feed.filters.food', 'Food & beverage') },
    { key: 'beauty', label: t('creator:feed.filters.beauty', 'Beauty & wellness') },
    { key: 'travel', label: t('creator:feed.filters.travel', 'Travel') },
    { key: 'fashion', label: t('creator:feed.filters.fashion', 'Fashion') },
    { key: 'lifestyle', label: t('creator:feed.filters.lifestyle', 'Lifestyle') }
  ]), [t]);

  const visibleOffers = useMemo(() => {
    const source = [
      ...offers.map(offer => ({ offer, status: 'available' })),
      ...appliedOffers.map(offer => ({ offer, status: 'applied' })),
      ...expiredOffers.map(offer => ({ offer, status: 'expired' }))
    ];
    const normalizedQuery = query.trim().toLowerCase();
    return source
      .filter(({ offer, status }) => {
        const matchesTab = activeTab === 'all' || status === activeTab;
        const matchesQuery = !normalizedQuery || [offer.title, offer.business_name, offer.country_name, offer.type_display]
          .filter(Boolean).some(value => value.toLowerCase().includes(normalizedQuery));
        const matchesCategory = categoryFilter === 'all' || getOfferCategories(offer).has(categoryFilter);
        return matchesTab && matchesQuery && matchesCategory;
      })
      .sort((a, b) => {
        if (sortBy === 'soon') {
          const aDate = new Date(a.offer.expires_at || a.offer.to_date || '9999-12-31').getTime();
          const bDate = new Date(b.offer.expires_at || b.offer.to_date || '9999-12-31').getTime();
          return aDate - bDate;
        }
        return new Date(b.offer.created_at || 0).getTime() - new Date(a.offer.created_at || 0).getTime();
      });
  }, [activeTab, appliedOffers, categoryFilter, expiredOffers, offers, query, sortBy]);

  const activeTabMeta = tabOptions.find(tab => tab.key === activeTab) || tabOptions[0];
  const emptyMessage = t(`creator:feed.empty.${activeTab}`, { defaultValue: t('creator:feed.empty.no_results') });
  const visibleAvailable = visibleOffers.filter(item => item.status === 'available');
  const visibleApplied = visibleOffers.filter(item => item.status === 'applied');
  const visibleExpired = visibleOffers.filter(item => item.status === 'expired');
  const openOffersCount = offers.filter(offer => !offer.is_full && offer.availability_status !== 'full').length;

  const toggleSaved = async offerId => {
    if (savingOffers.has(offerId)) return;
    const shouldSave = !savedOffers.has(offerId);
    setSavingOffers(previous => new Set([...previous, offerId]));
    setSavedOffers(previous => {
      const next = new Set(previous);
      if (shouldSave) next.add(offerId); else next.delete(offerId);
      return next;
    });
    try {
      const response = await browserFetch(`/api/feed/${offerId}/save/`, { method: shouldSave ? 'POST' : 'DELETE' });
      if (!response.ok) throw new Error('No se pudo actualizar la collab guardada');
    } catch (err) {
      console.error(err);
      setSavedOffers(previous => {
        const next = new Set(previous);
        if (shouldSave) next.delete(offerId); else next.add(offerId);
        return next;
      });
    } finally {
      setSavingOffers(previous => {
        const next = new Set(previous);
        next.delete(offerId);
        return next;
      });
    }
  };

  const renderCards = items => (
    <div className={styles['cards-grid']}>
      {items.map(({ offer, status }, cardIndex) => (
        <CollabCard key={offer.id} offer={offer} timeText={calculateTimeRemaining(offer)}
          category={t(`collab:types.${offer.type}`, { defaultValue: offer.type_display || t('creator:feed.card.category_fallback') })}
          isApplied={status === 'applied'} isExpired={status === 'expired'}
          isFull={!['applied', 'expired'].includes(status) && (offer.is_full || offer.availability_status === 'full')}
          isLocked={status === 'available' && !verificationStatus?.is_verified && cardIndex >= 3 && !offer.is_full && offer.availability_status !== 'full'}
          isSaved={savedOffers.has(offer.id)} isSaving={savingOffers.has(offer.id)} onToggleSaved={() => toggleSaved(offer.id)}
          onClick={() => navigate(`/creator/collabs/${offer.id}`)} />
      ))}
    </div>
  );

  if (loadingInit) return (
    <div className={styles['feed-page']}><div className={styles.container}><div className={styles['feed-loading']}>
      <Skeleton width="44%" height="24px" /><Skeleton width="64%" height="64px" />
      <div className={styles['cards-grid']}><Skeleton height="420px" /><Skeleton height="420px" /><Skeleton height="420px" /></div>
    </div></div></div>
  );

  return (
    <div className={styles['feed-page']}>
      <div className={styles.container}>
        {currentUser && !currentUser.is_profile_completed && <div className={styles['profile-nudge-banner']}>
          <div className={styles['nudge-content']}><ShieldAlert className={styles['nudge-icon']} size={20} /><div className={styles['nudge-text']}><strong>{t('creator:feed.hero.profile_incomplete')}</strong><span>{t('creator:feed.hero.profile_incomplete_help')}</span></div></div>
          <button className={styles['nudge-button']} onClick={() => navigate('/complete-profile')} type="button">{t('creator:feed.hero.complete_now')} <ArrowRight size={16} /></button>
        </div>}

        <section className={styles['hero-section']}>
          <div className={styles['hero-inner']}><div className={styles['gradient-overlay']} />
            <div className={styles['hero-copy']}>
              <div className={styles['badge-discover']}><Sparkles size={14} /> {t('creator:feed.hero.discover')}</div>
              <h1>{t('creator:feed.hero.title')}</h1><p>{t('creator:feed.hero.subtitle')}</p>
              <div className={styles['hero-actions']}>
                <Button size="md" className={styles['hero-primary']} onClick={() => document.getElementById('feed-results')?.scrollIntoView({ behavior: 'smooth' })}>{t('creator:feed.hero.explore', 'Explorar oportunidades')} <ArrowRight size={16} /></Button>
                <button className={styles['hero-secondary']} type="button" onClick={() => document.getElementById('feed-results')?.scrollIntoView({ behavior: 'smooth' })}>{t('creator:feed.hero.how_it_works', 'Cómo funciona')} <ChevronRight size={16} /></button>
              </div>
            </div>
            <aside className={styles['hero-stats']} aria-label={t('creator:feed.hero.summary_label', 'Resumen de oportunidades')}>
              <div className={styles['stat-top']}><span>{t('creator:feed.hero.week_label', 'Tu semana en FOTH')}</span><span className={styles['live-dot']}>{t('creator:feed.hero.live', 'En vivo')}</span></div>
              <div className={styles['stat-main']}><strong>{openOffersCount}</strong><span>{t('creator:feed.hero.active_label', 'collabs con cupos disponibles')}</span></div>
              <div className={styles['stat-footer']}><span>{t('creator:feed.hero.updated_label', 'Descubrimiento actualizado')}</span><strong>{t('creator:feed.hero.ready_label', 'Listo para explorar')}</strong></div>
            </aside>
          </div>
        </section>

        <div className={styles['toolbar-wrap']} id="feed-results">
          <div className={styles.toolbar} role="search">
            <div className={styles['search-input-wrapper']}><Search size={18} className={styles['search-icon']} /><input type="search" aria-label={t('creator:feed.hero.search_placeholder')} placeholder={t('creator:feed.hero.search_placeholder')} value={query} onChange={event => setQuery(event.target.value)} />{query && <button className={styles['clear-search']} type="button" aria-label={t('creator:feed.filters.clear_search', 'Limpiar búsqueda')} onClick={() => setQuery('')}><X size={15} /></button>}</div>
            <div className={styles['select-wrap']}><select className={styles['country-select']} aria-label={t('creator:feed.filters.country', 'Filtrar por país')} value={selectedCountry} onChange={handleCountryChange}><option value="">{t('creator:feed.filters.all_countries', 'Todos los países')}</option>{countries.map(country => <option key={country.id} value={country.id}>{country.name}</option>)}</select><ChevronDown size={16} /></div>
            <div className={styles['select-wrap']}><select className={styles['sort-select']} aria-label={t('creator:feed.filters.sort', 'Ordenar oportunidades')} value={sortBy} onChange={event => setSortBy(event.target.value)}><option value="new">{t('creator:feed.filters.newest', 'Más recientes')}</option><option value="soon">{t('creator:feed.filters.soonest', 'Más urgentes')}</option></select><ChevronDown size={16} /></div>
          </div>
          <div className={styles['tabs-card']} role="tablist" aria-label={t('creator:feed.tabs.label')}>
            {tabOptions.map(tab => <button key={tab.key} type="button" role="tab" aria-selected={activeTab === tab.key} className={clsx(activeTab === tab.key && styles.active)} onClick={() => setActiveTab(tab.key)}><span className={styles['tab-label']}>{tab.label}</span><span className={styles['tab-count']}>{tab.count}</span></button>)}
          </div>
          <div className={styles['category-filters']} aria-label={t('creator:feed.filters.categories', 'Filtrar por categoría')}><span className={styles['filter-label']}>{t('creator:feed.filters.label', 'Filtrar por')}</span>{categoryFilters.map(filter => <button key={filter.key} type="button" className={clsx(styles.chip, categoryFilter === filter.key && styles.active)} onClick={() => setCategoryFilter(filter.key)}>{filter.label}</button>)}</div>
        </div>

        <section className={styles['section-wrapper']}>
          <div className={styles['section-header']}><div><div className={styles['section-kicker']}>{activeTab === 'available' ? t('creator:feed.sections.discovery_kicker', 'Descubrimiento') : t('creator:feed.sections.followup_kicker', 'Sigue tu recorrido')}</div><h2>{activeTabMeta.label}</h2><p>{activeTabMeta.help}</p></div><div className={styles.stat}><SlidersHorizontal size={14} /><strong>{visibleOffers.length}</strong> {t('creator:feed.sections.visible', 'visibles')}</div></div>
          {loading ? <div className={styles['cards-grid']}><Skeleton height="420px" /><Skeleton height="420px" /><Skeleton height="420px" /></div> : !visibleOffers.length ? <div className={styles['empty-state']}><TrendingUp className={styles['empty-icon']} size={42} /><p>{emptyMessage}</p></div> : activeTab === 'applied' ? <div className={styles['applications-list']} id="feed-applications">{visibleApplied.map(({ offer }) => <ApplicationRow key={offer.id} offer={offer} onClick={() => navigate(`/creator/collabs/${offer.id}`)} t={t} />)}</div> : activeTab === 'all' ? <div className={styles['feed-sections']}>
            {!!visibleAvailable.length && <div><div className={styles['subsection-heading']}><h3>{t('creator:feed.sections.available', 'Disponibles para ti')}</h3><span>{visibleAvailable.length}</span></div>{renderCards(visibleAvailable)}</div>}
            {!!visibleApplied.length && <div><div className={styles['subsection-heading']}><h3>{t('creator:feed.sections.applied', 'Tus aplicaciones')}</h3><span>{visibleApplied.length}</span></div><div className={styles['applications-list']}>{visibleApplied.map(({ offer }) => <ApplicationRow key={offer.id} offer={offer} onClick={() => navigate(`/creator/collabs/${offer.id}`)} t={t} />)}</div></div>}
            {!!visibleExpired.length && <div><div className={styles['subsection-heading']}><h3>{t('creator:feed.sections.expired', 'Recientemente expiradas')}</h3><span>{visibleExpired.length}</span></div>{renderCards(visibleExpired)}</div>}
          </div> : renderCards(visibleOffers)}
        </section>
      </div>
    </div>
  );
};

const ApplicationRow = ({ offer, onClick, t }) => {
  const applicationStatus = offer.application_status || 'applied';
  const statusConfig = {
    applied: { label: t('creator:feed.card.applied', 'Aplicada'), tone: 'pending' },
    accepted: { label: t('creator:feed.card.accepted', 'Aceptada'), tone: 'accepted' },
    rejected: { label: t('creator:feed.card.rejected', 'No seleccionada'), tone: 'rejected' },
    cancelled: { label: t('creator:feed.card.cancelled', 'Cancelada'), tone: 'cancelled' },
    completed: { label: t('creator:feed.card.completed', 'Completada'), tone: 'completed' }
  }[applicationStatus] || { label: t('creator:feed.card.applied', 'Aplicada'), tone: 'pending' };
  return <article className={styles['application-card']}><div className={styles['application-thumb']}><img src={offer.featured_image_url || offer.featured_image} alt="" /></div><div className={styles['application-content']}><div className={styles['application-badges-row']}><span className={clsx(styles.status, styles[`status-${statusConfig.tone}`])}>{statusConfig.label}</span><CollabTypeBadge offer={offer} size="sm" /></div><h3>{offer.title}</h3><p>{offer.business_name || t('creator:feed.card.business_fallback', 'Empresa')} · {offer.country_name || t('creator:feed.card.location_fallback', 'Global')}</p></div><Button variant="outline" size="sm" onClick={onClick}>{t('creator:feed.card.view_status', 'Ver estado')} <ChevronRight size={15} /></Button></article>;
};

const CollabCard = ({ offer, isApplied, isExpired, isFull, isLocked, isSaved, isSaving, timeText, category, onToggleSaved, onClick }) => {
  const { t } = useTranslation('creator');
  const value = formatOfferValue(offer);
  return <article className={clsx(styles['collab-card'], isExpired && styles['expired-card'], isFull && styles['full-card'])}><div className={styles['card-media']}><img src={offer.featured_image_url || offer.featured_image} className={styles['card-bg-img']} alt={offer.title} /><div className={styles['card-gradient']} /><div className={styles['card-top-info']}><div className={styles['brand-pill']}>{offer.business_logo_url ? <img src={offer.business_logo_url} className={styles['brand-avatar']} alt="" /> : <span className={styles['brand-initial']}>{(offer.business_name || 'F').slice(0, 1)}</span>}<span>{offer.business_name || t('feed.card.business_fallback', 'Empresa')}</span></div><div className={styles['card-top-actions']}>{!isExpired && <button type="button" className={clsx(styles['save-button'], isSaved && styles.saved)} onClick={onToggleSaved} aria-label={isSaved ? t('feed.card.unsave', 'Quitar de guardados') : t('feed.card.save', 'Guardar collab')} aria-pressed={isSaved} disabled={isSaving}><Bookmark size={16} /></button>}{isApplied && <Badge variant="invite" className={styles['applied-badge']}><CheckCircle2 size={12} />{t('feed.card.applied')}</Badge>}{isLocked && <div className={styles['locked-icon']}><AlertCircle size={14} /></div>}</div></div>{isExpired && <div className={styles['expired-label-center']}><div className={styles['label-box']}><Clock3 size={24} /><div>{t('feed.card.expired')}</div></div></div>}{isFull && !isExpired && <div className={styles['full-label-center']}><div className={styles['label-box']}><LockKeyhole size={22} /><strong>{t('feed.card.full')}</strong><span>{t('feed.card.full_help')}</span></div></div>}{isLocked && <div className={styles['locked-overlay']}><AlertCircle size={32} /><div>{t('feed.card.network_access')}</div><p>{t('feed.card.locked_help')}</p><Button variant="primary" size="sm">{t('feed.card.validate_now')}</Button></div>}<div className={styles['card-footer']}><div className={styles['card-badges-row']}><div className={styles['category-badge']}>{category}</div><CollabTypeBadge offer={offer} size="sm" /></div><h3>{offer.title}</h3><div className={styles['meta-grid']}>{!isExpired && !isApplied && <div className={clsx(styles['meta-item'], styles.highlight)}><TimerReset size={14} /> {timeText}</div>}<div className={styles['meta-row']}><div className={styles['meta-item']}><Users size={14} /> {t('feed.card.creators_count', { count: offer.applications_count || 0, total: offer.quantity || 0 })}</div><div className={styles['meta-item']}><Gem size={14} /> {value || t('feed.card.value_pending', 'Valor por definir')}</div></div><div className={styles['meta-item']}><MapPin size={14} /> {offer.country_name || t('feed.card.location_fallback')}</div></div>{!isExpired && <Button variant={isFull ? 'outline' : 'primary'} fullWidth className={clsx(styles['card-cta'], isFull && styles['full-card-cta'])} onClick={onClick}>{isFull ? t('feed.card.full_cta') : isApplied ? t('feed.card.view_status', 'Ver estado') : t('feed.card.view_collab')} <ChevronRight size={16} /></Button>}</div></div></article>;
};

export default Feed;
